import { POST } from '@core-ui/api';
import { Loader } from '@core-ui/loader';
import analytics from 'app/analytics';
import { COLORS } from 'constants/styles';
import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import ArrowIcon from 'svg/ArrowIcon';
import Button from '../Button';
import { closeModal } from '../Modals/actions';
import { openNotification } from '../Notification/actions';
import { BUSINESS_LIST, COUNTRY_LIST } from './consts';
import useStyles, { useInputStyles, useSelectStyles } from './styles';

interface IInputProps {
  name?: string;
  label: string;
  value?: string;
  onChange?: (value: string) => void;
}

const Input: React.FC<IInputProps> = (props) => {
  const { name, label, value, onChange } = props;
  const classes = useInputStyles();

  const handleChange = (e: SyntheticEvent) => {
    if (onChange) {
      onChange((e.target as HTMLInputElement).value);
    }
  };

  return (
    <div className={classes.root}>
      <label className={classes.label}>{label}</label>
      <input name={name} className={classes.input} value={value} onChange={handleChange} />
    </div>
  );
};

interface ISelectProps {
  name?: string;
  label: string;
  options: string[];
  value?: string;
  onChange?: (value: string) => void;
}

const Select: React.FC<ISelectProps> = (props) => {
  const { name, label, options, value, onChange } = props;
  const classes = useSelectStyles();

  const handleChange = (e: SyntheticEvent) => {
    if (onChange) {
      onChange((e.target as HTMLSelectElement).value);
    }
  };

  return (
    <div className={classes.root}>
      <label className={classes.label}>{label}</label>
      <select name={name} defaultValue={options[0]} className={classes.select} onChange={handleChange}>
        {options.map((item, idx) => (
          <option key={item} value={item} disabled={idx === 0}>{item}</option>
        ))}
      </select>
    </div>
  );
};

interface IProps {
}

const ContactsForm: React.FC<IProps> = () => {
  const classes = useStyles();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [isCaptchaPassed, setCaptchaPassed] = useState(false);
  const captchaId = useMemo(() => `g_captcha_${Math.random()}`, []);
  const disabled = !name || !email || !/.+@.+\..+/.test(email) || !country || !businessType || !isCaptchaPassed;
  const dispatch = useDispatch();

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    if (disabled || isLoading) {
      return;
    }

    const data = Object.fromEntries(new FormData(event.target as HTMLFormElement));
    analytics.sendEvent(
      'CONTACTS',
      { name: data.name, email: data.email, country: data.country, business_type: data.business_type }
    );
    setLoading(true);
    POST('/ocean/sales_request', data).then(() => {
      dispatch(openNotification({ type: 'success', message: 'We will connect with you soon.' }));
      dispatch(closeModal());
    }).catch((e) => {
      const message = e.data?.detail?.[0]?.msg ?? 'Ooooops. Error, try sending the message again';
      dispatch(openNotification({ type: 'error', message }));
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    window.grecaptcha.ready(() => {
      window.grecaptcha.render(captchaId, {
        'sitekey': CAPTCHA_KEY,
        'theme': 'light',
        'callback': (response: string | unknown) => {
          setCaptchaPassed(Boolean(response));
        },
        'error-callback	': () => {
          setCaptchaPassed(false);
        },
        'expired-callback': () => {
          setCaptchaPassed(false);
        },
      });
    });
  }, [captchaId]);

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <div className={classes.root}>
        <div className={classes.inputs}>
          <Input name="name" label="Your name" value={name} onChange={setName} />
          <Input name="email" label="Email" value={email} onChange={setEmail} />
          <Select name="country" label="Country" options={COUNTRY_LIST} value={country} onChange={setCountry} />
          <Select
            name="business_type"
            label="Business type"
            options={BUSINESS_LIST}
            value={businessType}
            onChange={setBusinessType}
          />
        </div>

        <div style={{ width: '100%' }} id={captchaId} />

        <Button type="submit" disabled={disabled}>
          Submit
          <ArrowIcon fill={disabled ? COLORS.textGray : COLORS.white} />
        </Button>
      </div>

      {isLoading && (
        <div className={classes.loader}>
          <Loader />
        </div>
      )}
    </form>
  );
};

export default ContactsForm;
